/**
 * StorageLock - Async locking mechanism for cross-tab synchronization.
 *
 * Used for critical sections like PKCE rotation and token refresh.
 *
 * `withLock` prefers the built-in Web Locks API (`navigator.locks`) when it
 * is available. This gives us a browser-managed, FIFO-fair, no-polling
 * mutex that automatically releases if the holder tab is closed. When the
 * API is missing (older browsers, non-secure contexts), we transparently
 * fall back to the legacy localStorage-polling implementation so existing
 * call sites do not need to change.
 *
 * The imperative `tryAcquire` / `acquire` / `release` / `isLocked` /
 * `ownsLock` methods remain backed by localStorage: the Web Locks API is
 * intentionally callback-scoped and has no imperative `acquire`/`release`,
 * and these APIs are only exercised by the legacy test suite.
 */

/** Default lock expiry timeout in ms - auto-releases stale locks */
const DEFAULT_LOCK_TIMEOUT = 5000;
/** Default retry interval in ms */
const DEFAULT_RETRY_INTERVAL = 50;
/** Default max wait time in ms */
const DEFAULT_MAX_WAIT_TIME = 10000;
/** Default storage key prefix */
const DEFAULT_KEY_PREFIX = 'tc_lock_';

export interface StorageLockOptions {
  /** Lock expiry timeout in ms - auto-releases stale locks */
  lockTimeout?: number;
  /** Retry interval in ms */
  retryInterval?: number;
  /** Max wait time in ms */
  maxWaitTime?: number;
  /** Storage key prefix */
  keyPrefix?: string;
  /**
   * Force the localStorage-polling implementation of `withLock` even when
   * `navigator.locks` is available. Primarily used by tests that need to
   * exercise the legacy code path; production callers should leave this
   * unset.
   */
  useLegacyLock?: boolean;
}

/**
 * Minimal subset of the Web Locks API surface we use. Declared locally so
 * that older TypeScript `lib.dom` definitions that lack `navigator.locks`
 * still compile, and so that tests can stub it by overriding
 * `(navigator as any).locks`.
 */
interface WebLocksManager {
  request<T>(
    name: string,
    callback: (lock: unknown) => Promise<T> | T
  ): Promise<T>;
  request<T>(
    name: string,
    options: {
      mode?: 'exclusive' | 'shared';
      ifAvailable?: boolean;
      signal?: AbortSignal;
    },
    callback: (lock: unknown) => Promise<T> | T
  ): Promise<T>;
}

function getWebLocksManager(): WebLocksManager | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const locks = (navigator as unknown as { locks?: WebLocksManager }).locks;
  return locks && typeof locks.request === 'function' ? locks : null;
}

interface LockData {
  tabId: string;
  timestamp: number;
}

/**
 * Cross-tab storage lock implementation
 *
 * Ensures only one tab can perform critical operations (like PKCE rotation)
 * at a time, preventing race conditions in multi-tab scenarios.
 */
export class StorageLock {
  private readonly options: Required<StorageLockOptions>;
  private readonly tabId: string;
  private readonly webLocks: WebLocksManager | null;

  constructor(options: StorageLockOptions = {}) {
    this.options = {
      lockTimeout: options.lockTimeout ?? DEFAULT_LOCK_TIMEOUT,
      retryInterval: options.retryInterval ?? DEFAULT_RETRY_INTERVAL,
      maxWaitTime: options.maxWaitTime ?? DEFAULT_MAX_WAIT_TIME,
      keyPrefix: options.keyPrefix ?? DEFAULT_KEY_PREFIX,
      useLegacyLock: options.useLegacyLock ?? false,
    };
    this.tabId = this.generateTabId();
    this.webLocks = this.options.useLegacyLock ? null : getWebLocksManager();
  }

  /**
   * Generates a unique tab ID
   */
  private generateTabId(): string {
    // eslint-disable-next-line sonarjs/pseudo-random -- tab id uniqueness only, not security-sensitive
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Gets the full storage key for a lock
   */
  private getStorageKey(lockName: string): string {
    return `${this.options.keyPrefix}${lockName}`;
  }

  /**
   * Checks if a lock is stale (expired)
   */
  private isLockStale(lockData: LockData): boolean {
    return Date.now() - lockData.timestamp > this.options.lockTimeout;
  }

  /**
   * Attempts to acquire a lock
   * @param lockName Name of the lock to acquire
   * @returns true if lock was acquired, false otherwise
   */
  public tryAcquire(lockName: string): boolean {
    const key = this.getStorageKey(lockName);
    const existingLock = localStorage.getItem(key);

    if (existingLock) {
      try {
        const lockData: LockData = JSON.parse(existingLock);

        // Check if we already own the lock
        if (lockData.tabId === this.tabId) {
          // Refresh our lock timestamp
          this.refreshLock(lockName);
          return true;
        }

        // Check if the lock is stale
        if (this.isLockStale(lockData)) {
          // Stale lock - take ownership
          this.writeLock(lockName);
          return true;
        }

        // Lock is held by another tab and not stale
        return false;
      } catch {
        // Corrupted lock data - take ownership
        this.writeLock(lockName);
        return true;
      }
    }

    // No existing lock - acquire it
    this.writeLock(lockName);

    // Double-check we got the lock (handle race with other tabs)
    const verifyLock = localStorage.getItem(key);
    if (verifyLock) {
      try {
        const lockData: LockData = JSON.parse(verifyLock);
        return lockData.tabId === this.tabId;
      } catch {
        return false;
      }
    }

    return false;
  }

  /**
   * Writes lock data to storage
   */
  private writeLock(lockName: string): void {
    const key = this.getStorageKey(lockName);
    const lockData: LockData = {
      tabId: this.tabId,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(lockData));
  }

  /**
   * Refreshes the lock timestamp (to prevent expiry during long operations)
   */
  private refreshLock(lockName: string): void {
    this.writeLock(lockName);
  }

  /**
   * Acquires a lock, waiting if necessary
   * @param lockName Name of the lock to acquire
   * @returns Promise that resolves to true if lock was acquired, false if timeout
   */
  public async acquire(lockName: string): Promise<boolean> {
    const startTime = Date.now();

    while (Date.now() - startTime < this.options.maxWaitTime) {
      if (this.tryAcquire(lockName)) {
        return true;
      }

      // Wait before retrying
      await this.sleep(this.options.retryInterval);
    }

    // Timeout - could not acquire lock
    return false;
  }

  /**
   * Releases a lock
   * @param lockName Name of the lock to release
   */
  public release(lockName: string): void {
    const key = this.getStorageKey(lockName);
    const existingLock = localStorage.getItem(key);

    if (existingLock) {
      try {
        const lockData: LockData = JSON.parse(existingLock);
        // Only release if we own the lock
        if (lockData.tabId === this.tabId) {
          localStorage.removeItem(key);
        }
      } catch {
        // Corrupted data - safe to remove
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Executes a function while holding a lock.
   *
   * Prefers the Web Locks API (`navigator.locks.request`) when available:
   *  - browser-managed FIFO-fair mutex
   *  - auto-released if the holder tab is closed
   *  - no polling / no stale-lock cleanup needed
   *  - timeout is implemented with an `AbortSignal`
   *
   * Falls back to the localStorage polling implementation when
   * `navigator.locks` is not available (older browsers / non-secure
   * contexts).
   *
   * @param lockName Name of the lock
   * @param fn Function to execute while holding the lock
   * @returns Result of the function, or throws if lock could not be acquired
   */
  public async withLock<T>(
    lockName: string,
    fn: () => T | Promise<T>
  ): Promise<T> {
    if (this.webLocks) {
      return this.withWebLock(lockName, fn);
    }

    const acquired = await this.acquire(lockName);

    if (!acquired) {
      throw new Error(`Failed to acquire lock: ${lockName}`);
    }

    try {
      return await fn();
    } finally {
      this.release(lockName);
    }
  }

  /**
   * Web Locks API implementation of `withLock`. A timeout is implemented
   * via `AbortSignal` on the lock request so a stuck holder can't block
   * indefinitely; the maxWaitTime matches the legacy path's semantics.
   *
   * The signal only cancels queued lock requests — it does not interrupt an
   * in-progress callback. We therefore track acquisition separately and
   * clear the acquisition timer once the callback starts so a slow `fn()`
   * cannot be misclassified as an acquire timeout.
   */
  private async withWebLock<T>(
    lockName: string,
    fn: () => T | Promise<T>
  ): Promise<T> {
    const controller = new AbortController();
    let lockAcquired = false;
    const timeoutId = setTimeout(
      () => controller.abort(),
      this.options.maxWaitTime
    );
    const scopedName = `${this.options.keyPrefix}${lockName}`;
    try {
      return await this.webLocks!.request(
        scopedName,
        { mode: 'exclusive', signal: controller.signal },
        async () => {
          lockAcquired = true;
          clearTimeout(timeoutId);
          return fn();
        }
      );
    } catch (error) {
      if (!lockAcquired && controller.signal.aborted) {
        throw new Error(`Failed to acquire lock: ${lockName}`);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Checks if a lock is currently held (by any tab)
   * @param lockName Name of the lock to check
   * @returns true if lock is held and not stale
   */
  public isLocked(lockName: string): boolean {
    const key = this.getStorageKey(lockName);
    const existingLock = localStorage.getItem(key);

    if (!existingLock) {
      return false;
    }

    try {
      const lockData: LockData = JSON.parse(existingLock);
      return !this.isLockStale(lockData);
    } catch {
      return false;
    }
  }

  /**
   * Checks if this tab owns a specific lock
   * @param lockName Name of the lock to check
   * @returns true if this tab owns the lock
   */
  public ownsLock(lockName: string): boolean {
    const key = this.getStorageKey(lockName);
    const existingLock = localStorage.getItem(key);

    if (!existingLock) {
      return false;
    }

    try {
      const lockData: LockData = JSON.parse(existingLock);
      return lockData.tabId === this.tabId && !this.isLockStale(lockData);
    } catch {
      return false;
    }
  }

  /**
   * Sleep helper
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/** Default lock name for PKCE operations */
export const PKCE_LOCK_NAME = 'pkce_rotation';
